<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
import { ref, watch } from 'vue'
import {
  ApplyCouponToCart,
  GetCartFromLocalStorageOrApi,
} from '@/services/CartService.ts'
import type { IAoeCart } from '@/types/CartTypes'
import { GetCartTotalDiscountAmount } from '@/services/CartTotalsService'
import { handleError } from '@/services/ErrorService.ts'
import { trackCouponApplied } from '@/analytics/index'
import { Button as KButton } from '@progress/kendo-vue-buttons'
import { TextBox as KTextBox } from '@progress/kendo-vue-inputs'
import { useLogger } from 'vue-logger-plugin'
import { setStoreLogger } from '@/logger.ts'
const logger = useLogger()
setStoreLogger(logger)

const props = defineProps<{
  cart: IAoeCart
}>()

const applyCouponVisible = ref(false)
const useCouponVisible = ref(true)
const removeCouponVisible = ref(false)
const couponCode = ref('')
const localCart = ref<IAoeCart | null>(props.cart)

updateLocalVariables()

function getAppliedCouponDiscount(cart: IAoeCart): number {
  const appliedDiscountFromEarnedDiscounts =
    cart.earnedDiscounts
      ?.filter(discount => discount.isApplied)
      .reduce((sum, discount) => sum + (discount.appliedAmount ?? 0), 0) ?? 0

  if (appliedDiscountFromEarnedDiscounts > 0) {
    return appliedDiscountFromEarnedDiscounts
  }

  return GetCartTotalDiscountAmount(cart)
}

function applyCouponClickHandler(event: Event) {
  logger.debug('applyCouponClickHandler', event)
  applyCoupon(couponCode.value)
}

function applyCoupon(couponCodeParam?: string) {
  logger.debug('applyCoupon couponCodeParam:', couponCodeParam)
  if (!localCart.value) {
    logger.warn('cart is null')
    return
  }
  const isApplyingCoupon = !!couponCodeParam
  ApplyCouponToCart({
    aoeCartIdToken: localCart.value.aoeCartIdToken,
    couponCode: couponCodeParam,
  })
    .then(response => {
      localCart.value = response.item
      if (isApplyingCoupon) {
        const discountValue = getAppliedCouponDiscount(response.item)
        trackCouponApplied(
          couponCodeParam!,
          discountValue,
          response.item.currencyId || 'EUR',
        )
      }
    })
    .catch(async error => {
      await handleError(error, t)
    })
    .finally(async () => {
      if (!localCart.value) {
        logger.warn('cart is null')
        return
      }
      localCart.value = await GetCartFromLocalStorageOrApi(
        localCart.value.aoeCartIdToken,
      )
    })
}

function removeCouponClickHandler(event: Event) {
  logger.debug('removeCouponClickHandler', event)
  applyCoupon('')
}

function useCouponClickHandler(event: Event) {
  console.log('useCouponClickHandler', event)
  useCouponVisible.value = false
  applyCouponVisible.value = true
}

function updateLocalVariables() {
  logger.debug('updateLocalVariables')
  if (localCart.value && localCart.value.couponCode) {
    couponCode.value = localCart.value.couponCode
  } else {
    couponCode.value = ''
  }
  if (couponCode.value) {
    removeCouponVisible.value = true
    useCouponVisible.value = false
    applyCouponVisible.value = false
  } else {
    removeCouponVisible.value = false
    useCouponVisible.value = true
    applyCouponVisible.value = false
  }
}

watch(
  () => props.cart,
  newValue => {
    logger.debug('watch props.cart newvalue:', newValue)
    localCart.value = newValue
    updateLocalVariables()
  },
)
</script>
<template>
  <div class="aoe-cart-coupon">
    <div class="col-12" v-if="useCouponVisible">
      <KButton :fill-mode="'link'" @click="useCouponClickHandler($event)">
        {{ t('buttons.UseCoupon') }}
      </KButton>
    </div>
    <div class="col-12" v-if="removeCouponVisible">
      <KButton :fill-mode="'link'" @click="removeCouponClickHandler($event)">
        {{ t('buttons.RemoveCoupon', [couponCode]) }}
      </KButton>
    </div>
    <div class="col-12" v-if="applyCouponVisible">
      <KTextBox v-model="couponCode" />
      <KButton :fill-mode="'link'" @click="applyCouponClickHandler($event)">
        {{ t('buttons.ApplyCoupon') }}
      </KButton>
    </div>
  </div>
</template>
